import { useCallback, useEffect, useState } from "react";
import { formatDateTime } from "../lib/format";
import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  Dialog,
  EmptyState,
  Input,
  SkeletonRows,
  useToast,
} from "./ui";
import type { BadgeTone } from "./ui";
import {
  fileStatusLabel,
  sourceLabel,
  versionService,
  type VersionDetail,
  type VersionItem,
} from "../services/version.service";

const tones: Record<string, BadgeTone> = {
  ai: "info",
  user: "neutral",
  system: "warn",
  restore: "ok",
};


/** Histórico de versões: ver diff, restaurar, criar checkpoint e baixar. */
export default function VersionsPanel({ projectId }: { projectId: string }) {
  const toast = useToast();
  const [versions, setVersions] = useState<VersionItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState<VersionDetail | null>(null);
  const [diff, setDiff] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<VersionItem | null>(null);
  const [checkpoint, setCheckpoint] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setVersions(await versionService.list(projectId));
    } catch {
      setVersions([]);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function openDetail(version: VersionItem) {
    setDiff(null);
    try {
      const data = await versionService.show(projectId, version.uuid);
      setDetail(data);
      const result = await versionService.diff(projectId, version.uuid);
      setDiff(result.diff);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Não foi possível abrir a versão.");
    }
  }

  async function createCheckpoint() {
    if (!checkpoint.trim()) return;
    setSaving(true);
    try {
      const version = await versionService.checkpoint(projectId, checkpoint.trim());
      toast.success(`Checkpoint criado: versão #${version.number}.`);
      setCheckpoint("");
      await load();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Nada para versionar agora.");
    } finally {
      setSaving(false);
    }
  }

  async function restore(version: VersionItem) {
    try {
      const result = await versionService.restore(projectId, version.uuid);
      toast.success(
        result.rebuild === "queued"
          ? `Versão #${version.number} restaurada. Reconstruindo o ambiente…`
          : `Versão #${version.number} restaurada.`,
      );
      await load();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Não foi possível restaurar.");
    } finally {
      setConfirming(null);
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-2">
        <Input
          label="Criar checkpoint"
          placeholder="Ex.: antes de mexer no financeiro"
          value={checkpoint}
          onChange={(event) => setCheckpoint(event.target.value)}
          className="min-w-[220px]"
        />
        <Button size="sm" onClick={createCheckpoint} loading={saving} disabled={!checkpoint.trim()}>
          Criar checkpoint
        </Button>
      </div>

      {loading && <SkeletonRows rows={3} />}

      {!loading && versions.length === 0 && (
        <EmptyState
          title="Nenhuma versão ainda"
          description="Cada alteração aprovada gera uma versão que pode ser restaurada."
        />
      )}

      <div className="space-y-2">
        {versions.map((version) => (
          <Card key={version.uuid}>
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-ink">
                  Versão {version.number}
                  <span className="ml-2 font-normal text-ink-muted">{version.description}</span>
                </p>
                <p className="mt-1 text-[11px] text-ink-muted">
                  {formatDateTime(version.created_at)} · {version.author ?? "Sistema"} ·{" "}
                  <span className="font-mono">{version.git_commit?.slice(0, 8) ?? "sem commit"}</span>
                </p>
              </div>
              <Badge tone={tones[version.source] ?? "neutral"}>{sourceLabel(version.source)}</Badge>
            </div>

            <div className="mt-3 flex flex-wrap gap-2">
              <Button size="sm" variant="secondary" onClick={() => openDetail(version)}>
                Ver diferenças
              </Button>
              <Button size="sm" variant="secondary" onClick={() => setConfirming(version)}>
                Restaurar
              </Button>
              <a
                href={versionService.downloadUrl(projectId, version.uuid)}
                className="inline-flex h-8 items-center rounded-md border border-line px-3 text-xs text-ink hover:border-brand hover:text-brand"
              >
                Baixar versão
              </a>
            </div>
          </Card>
        ))}
      </div>

      <Dialog
        open={detail !== null}
        onClose={() => setDetail(null)}
        size="lg"
        title={detail ? `Versão ${detail.number}` : "Versão"}
        description={detail?.description}
      >
        {detail && (
          <div className="space-y-3">
            <p className="text-xs text-ink-muted">
              {formatDateTime(detail.created_at)} · {detail.author ?? "Sistema"} ·{" "}
              {sourceLabel(detail.source)} ·{" "}
              <span className="font-mono">{detail.git_commit ?? "sem commit"}</span>
            </p>

            <div>
              <p className="mb-1 text-xs font-semibold text-ink">
                Arquivos alterados ({detail.files.length})
              </p>
              <ul className="max-h-40 space-y-0.5 overflow-y-auto font-mono text-[11px] text-ink-muted">
                {detail.files.length === 0 && <li>Nenhum arquivo registrado neste commit.</li>}
                {detail.files.map((file) => (
                  <li key={file.path}>
                    <span className="text-ink">{fileStatusLabel(file.status)}</span> · {file.path}
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <p className="mb-1 text-xs font-semibold text-ink">Diferenças</p>
              <pre className="max-h-[45vh] overflow-auto rounded-md border border-line bg-surface-raised p-3 font-mono text-[11px] leading-relaxed text-ink-muted">
                {diff === null ? "Carregando…" : diff || "Sem diferenças registradas."}
              </pre>
            </div>
          </div>
        )}
      </Dialog>

      <ConfirmDialog
        open={confirming !== null}
        title={`Restaurar a versão ${confirming?.number ?? ""}?`}
        description="O estado atual é salvo como uma nova versão antes de restaurar. Depois o ambiente é reconstruído, o build roda e o preview é atualizado."
        confirmLabel="Restaurar"
        onCancel={() => setConfirming(null)}
        onConfirm={() => (confirming ? restore(confirming) : undefined)}
      />
    </div>
  );
}
